/**
 * Nirvagoo brand logo. Two variants:
 *   <BrandMark size={28} />  — the pin-N symbol only (favicon-style usage), raster PNG
 *   <BrandLogo size={30} />  — full lockup with "STAY IN VACATION MODE" (header/footer/hero)
 *
 * BrandLogo is an inline SVG (vector-traced once from the studio artwork,
 * see logoPath.js) rendered with fill="currentColor", so it stays perfectly
 * crisp at any size — no raster scaling, no per-color image files. Color is
 * driven by the `color` sx below: black wordmark in light mode, gold in dark
 * mode. Pass tone="white" to force plain white regardless of theme — used on
 * the transparent hero navbar overlay, where the logo sits directly on a
 * photo rather than the theme background.
 */
import Box from "@mui/material/Box";
import { useTheme } from "@/contexts/ThemeContext";
import { LOGO_PATH_D, LOGO_VIEWBOX } from "./logoPath";

const MARK_RATIO = 400 / 400; // width / height
const LOGO_RATIO = 2172 / 724;

export function BrandMark({ size = 28, className = "", tone }) {
  const src = tone === "white" ? "/nirvagoo-mark-white.png" : "/nirvagoo-mark.png";
  return (
    <Box
      component="img"
      src={src}
      alt="Nirvagoo"
      className={className}
      sx={{ height: size, width: size * MARK_RATIO, display: "block" }}
    />
  );
}

export function BrandLogo({ size = 30, className = "", tone }) {
  const { theme } = useTheme();
  const height = Math.round(size * 1.3);
  const width = Math.round(height * LOGO_RATIO);
  const color = tone === "white" ? "#fff" : theme === "dark" ? "var(--gold-bright)" : "#141414";
  return (
    <Box
      component="svg"
      viewBox={LOGO_VIEWBOX}
      className={className}
      data-testid="brand-logo"
      role="img"
      aria-label="Nirvagoo — Stay in vacation mode"
      sx={{ height, width, display: "block", color, flexShrink: 0 }}
    >
      <path d={LOGO_PATH_D} fillRule="evenodd" fill="currentColor" />
    </Box>
  );
}
